/**
 * @fileoverview PHARMAKON — Trésorerie : `useTresorerieQuery`, la lecture d'un écran de trésorerie
 * branchée sur le store à clés du temps réel (TWP-11).
 * @module hooks/useTresorerieQuery
 *
 * 02_ARCHITECTURE §11, UX 01 §13. Un écran déclare CE QU'IL LIT (`ressource`, `params`, le
 * `charger` de son petit point d'accès) et CE DONT IL DÉPEND (`depend`, les clés publiées par le
 * serveur après commit : `compte:<id>`, `operations`, `kpi`…). Il est relu quand — et seulement
 * quand — l'une d'elles arrive (trame du flux unique de l'officine, ou réponse d'une écriture de cet
 * onglet via `appliquerEcritureTresorerie`), ou quand le plancher de version a détecté un écart.
 * Aucun sondage de données. Pendant une relecture la dernière donnée reste affichée
 * (`rafraichissement`), une erreur de relecture la garde aussi (`erreur` à côté).
 *
 * @example
 *   const { data, chargement, rafraichissement, erreur } = useTresorerieQuery({
 *     ressource: "compte",
 *     params: { id },
 *     depend: [cleTresorerie(FAMILLES_CLES_TRESORERIE.COMPTE, id)],
 *     charger: () => tresoreriePharmacieApi.compte(id),
 *   });
 */
import { useEffect, useRef } from "react";
import { useStore } from "zustand";
import { tresoreriePharmacieApi } from "../services/index.js";
import { ecouterSujet } from "../services/sujetsTempsReel.js";
import { cleRequete, creerStoreTresorerie } from "../lib/tresorerie/store.js";
import { useAuthStore } from "../stores/authStore.js";
import { can } from "../config/permissions.config.js";

/**
 * TWP-22 (RC-130, TA-75 (10)) — le plancher `GET /version` est réservé aux rôles qui lisent la trésorerie
 * de l'officine (`tresorerie_pharmacie:read`, la permission de la route). Un rôle de comptoir (CAISSIER)
 * n'appelle jamais une route qui lui répondrait 403 : il reçoit les trames de SON comptoir et relit sa
 * carte sur elles ; pour lui le plancher répond « inchangé » sans requête.
 */
export const peutLirePlancher = (user = useAuthStore.getState().user) => can(user, "tresorerie_pharmacie", "read");

/** LE store de trésorerie de l'application (un par onglet). */
export const storeTresorerie = creerStoreTresorerie({
  lireVersion: (p) => (peutLirePlancher() ? tresoreriePharmacieApi.version(p) : Promise.resolve({ inchange: true })),
  ecouter: ecouterSujet,
});

/**
 * L'écran qui vient d'écrire applique sa réponse (`touched`, `version_tresorerie` de toute réponse
 * d'écriture de la trésorerie) : les vues concernées de CET onglet se relisent tout de suite, sans
 * attendre la trame — qui ne les relira pas une seconde fois.
 * @param {{ touched?: string[], version_tresorerie?: string|null }} reponse
 */
export function appliquerEcritureTresorerie(reponse) {
  return storeTresorerie.appliquerEcriture(reponse ?? {});
}

/**
 * @param {object} p
 * @param {string} p.ressource
 * @param {object} [p.params]
 * @param {string[]} p.depend clés d'invalidation dont la vue dépend
 * @param {() => Promise<any>} p.charger lecture du point d'accès (rend la donnée)
 * @param {boolean} [p.actif=true] false : la vue ne lit rien (ex. tiroir fermé)
 * @returns {{ data: any, erreur: any, chargement: boolean, rafraichissement: boolean, perime: boolean, lu_at: number|null, recharger: () => Promise<void> }}
 */
export function useTresorerieQuery({ ressource, params = {}, depend = [], charger, actif = true }) {
  // Une chaîne stable : un `params` recréé à chaque rendu ne relit rien.
  const cle = cleRequete(ressource, params);
  const dependKey = depend.join("\u0000");
  const chargerRef = useRef(charger);
  // Le dernier chargeur rendu, posé AVANT l'effet de montage (les effets s'exécutent dans l'ordre).
  useEffect(() => {
    chargerRef.current = charger;
  });

  useEffect(() => {
    if (!actif) return undefined;
    return storeTresorerie.monter(cle, { charger: () => chargerRef.current(), depend });
    // `depend` est suivi par sa forme sérialisée ; le chargeur, par la référence.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cle, dependKey, actif]);

  const entree = useStore(storeTresorerie.store, (s) => s.entrees[cle]);
  return {
    data: actif ? entree?.data : undefined,
    erreur: entree?.erreur ?? null,
    chargement: Boolean(actif && (!entree || entree.statut === "vide" || entree.statut === "chargement") && entree?.data === undefined),
    rafraichissement: Boolean(entree?.rafraichit),
    perime: Boolean(entree?.perime),
    lu_at: entree?.lu_at ?? null,
    recharger: () => storeTresorerie.charger(cle),
  };
}

/** « À jour · 14:32 » (UX 01 §5.5) — la version lue au plancher et l'instant serveur de la lecture. */
export function useFraicheurTresorerie() {
  const version = useStore(storeTresorerie.store, (s) => s.version);
  const verifie_at = useStore(storeTresorerie.store, (s) => s.verifie_at);
  return { version, verifie_at };
}
